import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { sourceLabel } from '@/features/crm/leads/constants';
import { formatMoney } from '@/lib/money';
import { useChannels, useDashboard, useForecast } from '../queries';

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl tabular-nums">{value}</CardTitle>
        {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
      </CardHeader>
    </Card>
  );
}

export function CommandCenterPage() {
  const dashboard = useDashboard();
  const channels = useChannels();
  const forecast = useForecast();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Command Center</h1>
        <p className="text-muted-foreground text-sm">
          Cross-module snapshot for the active organization.
        </p>
      </div>

      {dashboard.isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : dashboard.isError ? (
        <p className="text-destructive">{(dashboard.error as unknown as ApiError).message}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="Open deals" value={String(dashboard.data.pipeline?.open_deals ?? 0)} hint="CRM pipeline" />
            <Stat
              label="Active projects"
              value={String(dashboard.data.projects.active)}
              hint="Delivery"
            />
            <Stat
              label="Tracked hours"
              value={String(Number(dashboard.data.projects.tracked_hours))}
              hint="All projects"
            />
            {forecast.isSuccess && forecast.data.runway_months != null ? (
              <Stat
                label="Runway"
                value={`${forecast.data.runway_months} mo`}
                hint="Net ÷ avg monthly burn"
              />
            ) : null}
          </div>

          {dashboard.data.finance ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Stat label="Income (USD)" value={formatMoney(dashboard.data.finance.income_base_usd, 'USD', { narrowSymbol: true }) ?? '—'} />
              <Stat label="Expenses (USD)" value={formatMoney(dashboard.data.finance.expense_base_usd, 'USD', { narrowSymbol: true }) ?? '—'} />
              <Stat label="Net (USD)" value={formatMoney(dashboard.data.finance.net_base_usd, 'USD', { narrowSymbol: true }) ?? '—'} />
              <Stat
                label="Outstanding AR (USD)"
                value={formatMoney(dashboard.data.finance.outstanding_ar_base_usd, 'USD', { narrowSymbol: true }) ?? '—'}
                hint="Issued receivables"
              />
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              Financial KPIs are hidden for your role.
            </p>
          )}
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Lead inflow by channel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead className="text-right">Leads</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {channels.isPending ? (
                  <TableRow>
                    <TableCell colSpan={2} className="py-6">
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                  </TableRow>
                ) : channels.isError ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-destructive py-8 text-center">
                      {(channels.error as unknown as ApiError).message}
                    </TableCell>
                  </TableRow>
                ) : channels.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-muted-foreground py-8 text-center">
                      No leads yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  channels.data.map((row) => (
                    <TableRow key={row.source}>
                      <TableCell>{sourceLabel(row.source)}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.leads}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
